import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, MessageSquarePlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { MessageBubble } from "@/components/chat/message-bubble";
import { assistantApi } from "@/lib/api/assistant";
import { ApiError } from "@/lib/api/client";
import {
  clearAuthToken,
  clearSessionId,
  getSessionId,
  setSessionId,
} from "@/lib/auth/storage";
import type { ChatMessage, HealthResponse } from "@/lib/types";
import { cn } from "@/lib/utils";

const STARTER_PROMPTS = [
  "How much did we raise today?",
  "Today's donations above ₹10,000",
  "Top donors last 30 days",
  "Recent web donations",
] as const;

export default function ChatPage() {
  const navigate = useNavigate();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionIdState] = useState<string | null>(() =>
    getSessionId()
  );
  const [loading, setLoading] = useState(false);
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [healthOk, setHealthOk] = useState<boolean | null>(null);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, loading, scrollToBottom]);

  useEffect(() => {
    let cancelled = false;

    async function fetchHealth() {
      try {
        const data = await assistantApi.health();
        if (cancelled) return;
        setHealth(data);
        setHealthOk(data.gemini_configured && data.erpnext_reachable);
      } catch {
        if (!cancelled) {
          setHealth(null);
          setHealthOk(false);
        }
      }
    }

    fetchHealth();
    const interval = window.setInterval(fetchHealth, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(interval);
    };
  }, []);

  function handleNewChat() {
    clearSessionId();
    setSessionIdState(null);
    setMessages([]);
    setInput("");
    textareaRef.current?.focus();
  }

  function handleLogout() {
    clearAuthToken();
    clearSessionId();
    navigate("/login", { replace: true });
  }

  async function sendMessage(text: string) {
    const trimmed = text.trim();
    if (!trimmed || loading) return;

    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: trimmed }]);
    setLoading(true);

    try {
      const response = await assistantApi.chat(trimmed, sessionId ?? undefined);
      setSessionIdState(response.session_id);
      setSessionId(response.session_id);
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: response.reply,
          tools_used: response.tools_used,
        },
      ]);
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { role: "assistant", content: `Error: ${message}` },
      ]);
    } finally {
      setLoading(false);
      textareaRef.current?.focus();
    }
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    void sendMessage(input);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      void sendMessage(input);
    }
  }

  return (
    <div className="flex min-h-screen flex-col">
      <header className="sticky top-0 z-10 border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="mx-auto flex max-w-4xl items-center justify-between gap-3 px-4 py-3">
          <div className="flex min-w-0 items-center gap-3">
            <h1 className="truncate text-lg font-semibold">Donations Assistant</h1>
            <div
              className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground"
              title={
                health
                  ? `Model: ${health.model} · Gemini: ${health.gemini_configured ? "ok" : "off"} · ERPNext: ${health.erpnext_reachable ? "ok" : "down"}`
                  : "Checking health…"
              }
            >
              <span
                className={cn(
                  "inline-block size-2.5 rounded-full",
                  healthOk === null
                    ? "bg-gray-300"
                    : healthOk
                      ? "bg-green-500"
                      : "bg-red-500"
                )}
                aria-hidden
              />
              <span className="hidden sm:inline">
                {healthOk === null
                  ? "Checking…"
                  : healthOk
                    ? "Healthy"
                    : "Degraded"}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleNewChat}
            >
              <MessageSquarePlus className="size-4" />
              <span className="hidden sm:inline">New chat</span>
            </Button>
            <Button type="button" variant="ghost" size="sm" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="mx-auto flex w-full max-w-4xl flex-1 flex-col px-4 py-4">
        <div className="flex-1 space-y-4 overflow-y-auto pb-4">
          {messages.length === 0 && !loading && (
            <div className="flex h-full min-h-[40vh] flex-col items-center justify-center text-center text-muted-foreground">
              <p className="text-sm">
                Ask about donations, donors, or recent activity.
              </p>
              <p className="mt-1 text-xs">
                Try a starter prompt below or type your own question.
              </p>
            </div>
          )}

          {messages.map((message, index) => (
            <MessageBubble key={index} message={message} />
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex items-center gap-2 rounded-2xl border bg-card px-4 py-3 text-sm text-muted-foreground shadow-sm">
                <Loader2 className="size-4 animate-spin" />
                Thinking…
              </div>
            </div>
          )}

          <div ref={messagesEndRef} />
        </div>

        <div className="sticky bottom-0 border-t bg-background/95 pb-4 pt-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="mb-3 flex flex-wrap gap-2">
            {STARTER_PROMPTS.map((prompt) => (
              <button
                key={prompt}
                type="button"
                disabled={loading}
                onClick={() => setInput(prompt)}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-foreground transition-colors hover:bg-accent disabled:opacity-50"
              >
                {prompt}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="flex gap-2">
            <textarea
              ref={textareaRef}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask about donations…"
              rows={2}
              disabled={loading}
              className="min-h-[2.75rem] flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            />
            <Button type="submit" disabled={loading || !input.trim()}>
              Send
            </Button>
          </form>
          <p className="mt-2 text-center text-xs text-muted-foreground">
            Enter to send · Shift+Enter for newline
          </p>
        </div>
      </main>
    </div>
  );
}
